/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import AtomicSDK, {
  AACHeadlessStreamContainer,
  NativePlatform,
} from "@atomic.io/action-cards-web-sdk";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { PushNotifications } from "@capacitor/push-notifications";
import { Badge } from "@capawesome/capacitor-badge";
import { AtomicTakeoverModal } from "@farmlands/atomic-io";
import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAccountSwitcher } from "../../features/azure-b2c/components/AccountSwitcherProvider";
import AuthSlice, { apiGetAtomicToken, selectAtomicToken } from "../../features/azure-b2c/storage";
import getDeviceInfo from "../../native/utils/Device";
import { useAppDispatch } from "../../store/rootStore";
import { error, info, log } from "../../util/log";
import { TDictionary } from "../../util/types";
import { selectConfigByKey } from "../firebase/remote-config/remoteConfig.slice";
import { usePushNotificationRegistrationToken } from "../push-notifications/PushNotificationsProvider";

import { openAppSettings } from "./utils/OpenAppSettings";
import { requestPermission } from "./utils/RequestPermission";
import AtomicDrawer from "./AtomicDrawer";

const ATOMIC_API_HOST = process.env.REACT_APP_ATOMIC_API_HOST!;
const ATOMIC_API_KEY = process.env.REACT_APP_ATOMIC_API_KEY!;
const ATOMIC_ENVIRONMENT_ID = process.env.REACT_APP_ATOMIC_ENVIRONMENT_ID!;

export const ATOMIC_STREAM_INBOX_ID = process.env.REACT_APP_ATOMIC_STREAM_CONTAINER_ID_INBOX!;
export const ATOMIC_STREAM_HOMEPAGE_ID = process.env.REACT_APP_ATOMIC_STREAM_CONTAINER_ID_HOMEPAGE!;
export const ATOMIC_STREAM_WHATS_NEW_ID =
  process.env.REACT_APP_ATOMIC_STREAM_CONTAINER_ID_WHATS_NEW!;
export const ATOMIC_STREAM_DASHBOARD_PROMOTIONS =
  process.env.REACT_APP_ATOMIC_STREAM_CONTAINER_ID_DASHBOARD_PROMOTIONS!;

export const ATOMIC_STREAM_TAKEOVER_ID = process.env.REACT_APP_ATOMIC_STREAM_CONTAINER_ID_TAKEOVER!;

const ATOMIC_TAKEOVER_STREAM_CONFIG = {
  cardMaximumWidth: 500,
  enabledUiElements: {
    cardListToast: false,
  },
};

// Helper function to decode JWT and check if it's nearly expired
const isTokenExpired = (token: string, bufferInSeconds = 3600): boolean => {
  const payload = JSON.parse(atob(token.split(".")[1]));
  const currentTime = Math.floor(Date.now() / 1000);

  // Check if the token is within the buffer time of expiration
  return payload.exp - currentTime <= bufferInSeconds;
};

// Create the context
interface AtomicContextType {
  atomicReady: boolean;
  atomicRegisteredForPush: boolean;
  atomicReadyForPushRegistration: boolean;
  messageCount: number;
  drawerOpen: boolean;
  drawerContainerId: string;
  setDrawerOpen: (containerId: string) => void;
  closeDrawer: () => void;
  openDrawer: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onLinkButtonPressed: (payload: any) => void;
}

const initialInboxCount = { cardCount: 0, totalCards: 0 };

const AtomicContext = React.createContext<AtomicContextType | undefined>(undefined);

export const setBadgeCount = (count: number) => {
  if (Capacitor.isNativePlatform()) {
    (async () => {
      if (
        (await Badge.isSupported()).isSupported &&
        // If you don't check this, it'll end up prompting the user
        (await PushNotifications.checkPermissions()).receive === "granted"
      ) {
        Badge.set({ count: count });
      }
    })();
  }
};
// AtomicProvider Component
interface AtomicProviderProps {
  children: React.ReactNode | React.ReactNode[];
}

const AtomicPlatforms = {
  ios: "iOS",
  android: "Android",
  // web: 'web',
} as TDictionary<NativePlatform>;

const AtomicProvider: React.FC<AtomicProviderProps> = ({ children }) => {
  const [atomicReady, setAtomicReady] = React.useState(false);
  const [atomicRegisteredForPush, setAtomicRegisteredForPush] = React.useState(false);
  const [atomicReadyForPushRegistration, setAtomicReadyForPushRegistration] = React.useState(false);
  const pushRegistrationToken = usePushNotificationRegistrationToken();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerContainerId, setDrawerContainerId] = React.useState<string>(ATOMIC_STREAM_INBOX_ID);

  const { accounts, onChange } = useAccountSwitcher();

  const dispatch = useAppDispatch();
  const atomicToken = useSelector(selectAtomicToken);
  const atomicTokenTimeout = useSelector(selectConfigByKey("authorisationTimeouts")).getAtomicToken;

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const streamId = searchParams.get("stream");
    try {
      if (streamId) {
        setDrawerOpenByContainerId(streamId);
      }
    } catch (err) {
      setDrawerOpenByContainerId(ATOMIC_STREAM_INBOX_ID);
      error("Error reading inbox stream id from url", err);
    } finally {
      if (streamId) {
        // Create a new instance of URLSearchParams to modify
        const newSearchParams = new URLSearchParams(searchParams);
        // Remove only the specific search parameters
        newSearchParams.delete("stream");
        // Update the search parameters without affecting other params
        setSearchParams(newSearchParams);
      }
    }
  }, [searchParams, setDrawerContainerId]);

  const getAtomicToken = async () => {
    if (atomicToken && !isTokenExpired(atomicToken)) {
      return atomicToken;
    } else {
      const value = await apiGetAtomicToken(atomicTokenTimeout);
      dispatch(AuthSlice.actions.setAtomicToken(value.token));
      localStorage.setItem("atomicJwt", value.token);
      return value.token;
    }
  };

  const setDrawerOpenByContainerId = (containerId: string) => {
    setDrawerContainerId(containerId);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    updateCardCount();
    setDrawerOpen(false);
  };

  const openDrawer = () => {
    setDrawerOpen(true);
  };

  const [{ cardCount }, setInboxCardCounts] = React.useState<{
    cardCount: number;
    totalCards: number;
  }>(initialInboxCount);

  const updateCardCount = useCallback(() => {
    AtomicSDK.requestUserMetrics()
      .then((metrics) => {
        const cardCount = metrics.unseenCardsForStreamContainer(ATOMIC_STREAM_INBOX_ID);
        const totalCards = metrics.totalCardsForStreamContainer(ATOMIC_STREAM_INBOX_ID);
        setInboxCardCounts({ cardCount, totalCards });
        setBadgeCount(cardCount);
      })
      .catch((err) => {
        error(err);
      });
  }, [setInboxCardCounts]);

  const inboxStream = React.useRef<AACHeadlessStreamContainer | undefined>(undefined);
  const navigate = useNavigate();

  const atomicEnabled = useSelector(selectConfigByKey("atomicEnabled"));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function onLinkButtonPressed(payload: any) {
    updateCardCount();
    if (payload.actionPayload.inbox) {
      setDrawerOpenByContainerId(payload.actionPayload.inbox);
    } else {
      closeDrawer();
    }

    if (payload.actionPayload.accountNumber) {
      const account = accounts.find(
        (acc) => acc.accountNumber === payload.actionPayload.accountNumber
      );
      if (account) {
        onChange(undefined, account.accountNumber);
      }
    }
    if (payload.actionPayload.path) {
      navigate(payload.actionPayload.path);
    } else if (payload.actionPayload.url) {
      window.open(payload.actionPayload.url, "_blank");
    }

    if (payload.actionPayload.androidSetting || payload.actionPayload.iosSetting) {
      openAppSettings(payload.actionPayload.androidSetting, payload.actionPayload.iosSetting);
    }

    if (payload.actionPayload.requestPermission) {
      requestPermission(payload.actionPayload.requestPermission);
    }
  }

  function disableAtomic() {
    inboxStream?.current?.stop();
    setInboxCardCounts(initialInboxCount);
    AtomicSDK.logout();
    setAtomicReady(false);
    setAtomicReadyForPushRegistration(false);
    setAtomicRegisteredForPush(false);
  }

  const userMemoTrigger = useMemo(() => {
    return (
      atomicReady && atomicToken && !isTokenExpired(atomicToken) && accounts && accounts.length > 0
    );
  }, [accounts, atomicReady, atomicToken]);

  useEffect(() => {
    try {
      if (userMemoTrigger) {
        getDeviceInfo().then((value) => {
          AtomicSDK.updateUser({
            profile: {
              ...value,
              accounts: accounts.map((account) => account.accountNumber).join(","),
              flp_last_open: new Date().toISOString(),
            },
          });
        });
      }
    } catch (err) {
      log(err);
    }
  }, [userMemoTrigger]);

  useEffect(() => {
    (async () => {
      if (!atomicEnabled && atomicReady) {
        disableAtomic();
        return;
      }
      if (!atomicEnabled) {
        return;
      }

      if (!atomicToken || isTokenExpired(atomicToken)) {
        getAtomicToken();
        return;
      }

      try {
        AtomicSDK.login(ATOMIC_API_HOST, ATOMIC_API_KEY, ATOMIC_ENVIRONMENT_ID, getAtomicToken, 5);
        // Register for push notifications for all cards.
        const device = await Device.getInfo();
        if (device.platform !== "web") {
          const platform = AtomicPlatforms[device.platform];
          const uuid = await Device.getId();
          const appId = (await App.getInfo()).id;

          AtomicSDK.setNativeDeviceInfo({
            platform: platform,
            deviceId: uuid.identifier,
            appId: appId,
          });

          AtomicSDK.registerStreamContainersForNotifications([ATOMIC_STREAM_INBOX_ID], true);

          setAtomicReadyForPushRegistration(true);
        }

        // Initialise and Observe inbox changes in headless mode
        inboxStream.current = AtomicSDK.observableStreamContainer({
          streamContainerId: ATOMIC_STREAM_INBOX_ID,
          features: {
            runtimeVariableAnalytics: false,
          },

          onCardCountChanged() {
            updateCardCount();
            return null;
          },
          cardFeedHandler() {
            return null;
          },
        });

        inboxStream.current?.start();

        // Get Initial Inbox card count
        updateCardCount();
        setAtomicReady(true);

        return () => {
          inboxStream.current?.stop();
          setInboxCardCounts(initialInboxCount);
          AtomicSDK.logout();
          setAtomicReady(false);
          setAtomicReadyForPushRegistration(false);
          setAtomicRegisteredForPush(false);
        };
      } catch {
        setAtomicReady(false);
        setAtomicReadyForPushRegistration(false);
        setAtomicRegisteredForPush(false);
      }
    })();

    // Cleanup on component unmount
  }, [atomicEnabled, atomicToken]);

  useEffect(() => {
    if (pushRegistrationToken && atomicReadyForPushRegistration) {
      if (pushRegistrationToken.value.length < 400) {
        AtomicSDK.registerDeviceForNotifications(pushRegistrationToken.value);
        setAtomicRegisteredForPush(true);
        info("Atomic is now registered for push notifications");
      } else {
        error("Push notification value over 400 Char: ", pushRegistrationToken.value);
      }
    }
  }, [atomicReadyForPushRegistration, pushRegistrationToken]);

  return (
    <AtomicContext.Provider
      value={{
        messageCount: cardCount,
        atomicReady,
        atomicRegisteredForPush,
        atomicReadyForPushRegistration,
        drawerOpen,
        drawerContainerId,
        closeDrawer,
        openDrawer,
        onLinkButtonPressed,
        setDrawerOpen: setDrawerOpenByContainerId,
      }}
    >
      <AtomicDrawer />
      <AtomicTakeoverModal
        containerId={ATOMIC_STREAM_TAKEOVER_ID}
        atomicReady={atomicReady}
        onLinkButtonPressed={onLinkButtonPressed}
        streamConfig={ATOMIC_TAKEOVER_STREAM_CONFIG}
      />
      {children}
    </AtomicContext.Provider>
  );
};

export const useAtomic = (): AtomicContextType => {
  const context = React.useContext(AtomicContext);
  if (!context) {
    throw new Error("useAtomic must be used within an AtomicProvider");
  }
  return context;
};

export default AtomicProvider;
